import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
// import "@splidejs/splide/css";
import "@splidejs/splide/css/core";

import { initHeader } from "./modules/header";
import { initPageTransitions } from "./modules/page-transition";
import { initPageEffects } from "./modules/page-effects";

gsap.registerPlugin(ScrollTrigger);

// ローディングアニメーション設定
const setupLoadingAnimation = () => {
  const loadElement = document.querySelector(
    "[data-gsap='load-hidden']"
  ) as HTMLElement;

  if (!loadElement) return;

  // ローディング中はスクロール無効化
  document.body.style.overflowX = "hidden";
  // スクロール位置をページトップにリセット
  window.scrollTo(0, 0);

  // 1秒後にアニメーション開始
  setTimeout(() => {
    // タイムライン作成
    const tl = gsap.timeline({
      onComplete: () => {
        // ローディングアニメーション完了後にスクロールを有効化
        document.body.style.overflowX = "";
      },
    });

    document.body.style.visibility = "visible";
    tl.to(loadElement, {
      clipPath: "inset(0% 0% 0% 100%)",
      duration: 0.4,
      ease: "power3.inOut",
    });
  }, 200);
};

let resizeTimeoutId: NodeJS.Timeout | null = null;

// Lenisインスタンス
let lenis: Lenis | undefined;

const isTouchDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  const mediaQuery =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(pointer: coarse)")
      : null;
  const hasTouchEvent =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  return Boolean(mediaQuery?.matches || hasTouchEvent);
};

const setScrollbarWidth = (): void => {
  const scrollbarWidth =
    window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.style.setProperty(
    "--scrollbar-width",
    `${scrollbarWidth}px`
  );
};

const setMinViewport = (): void => {
  const viewport = document.querySelector<HTMLMetaElement>(
    'meta[name="viewport"]'
  );
  if (!viewport) return;

  const MIN_VIEWPORT_SIZE = 416;
  const value =
    window.outerWidth > MIN_VIEWPORT_SIZE
      ? "width=device-width, initial-scale=1.0"
      : `width=${MIN_VIEWPORT_SIZE}`;
  viewport.setAttribute("content", value);
};

const updateViewport = (): void => {
  setScrollbarWidth();
  setMinViewport();
};

// リサイズハンドラー（デバウンス処理込み）
const handleResize = (): void => {
  if (resizeTimeoutId) clearTimeout(resizeTimeoutId);
  resizeTimeoutId = setTimeout(updateViewport, 100);
};
handleResize();

// Lenisの初期化
const initLenis = (): void => {
  // HTMLのみにLenisクラスを追加（bodyには付与しない）
  document.documentElement.classList.add("lenis");

  lenis = new Lenis({
    duration: 1.2, // アニメーション時間
    easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // イージング関数
    orientation: "vertical", // 縦スクロール
    gestureOrientation: "vertical", // ジェスチャーの方向
    smoothWheel: true, // ホイールスクロールをスムーズに
    wheelMultiplier: 1, // ホイールの速度
    touchMultiplier: 2, // タッチの速度
    syncTouch: true, // タッチとホイールの同期
  });

  // ScrollTriggerとの統合
  lenis.on("scroll", ScrollTrigger.update);

  // スクロール状態に応じてクラスを切り替え（htmlに付与）
  lenis.on("scroll", ({ velocity }) => {
    document.documentElement.classList.toggle(
      "lenis-scrolling",
      Math.abs(velocity) > 0.1
    );
  });

  // RAF（requestAnimationFrame）でLenisを更新
  function raf(time: number) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }

  requestAnimationFrame(raf);

  // GSAPでScrollTriggerを使用する際のスクローラーの設定
  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });

  gsap.ticker.lagSmoothing(0);
};

const fontLoadPromise = () =>
  new Promise<void>((resolve) => {
    (window as any).WebFontConfig = {
      google: { families: ["Montserrat:600", "Noto Sans JP:400,500,600"] },
      active: function () {
        sessionStorage.fonts = true;
        resolve();
      },
    };

    (function () {
      var wf = document.createElement("script");
      wf.src =
        "https://ajax.googleapis.com/ajax/libs/webfont/1.6.26/webfont.js";
      wf.type = "text/javascript";
      wf.async = true;
      var s = document.getElementsByTagName("script")[0];
      s.parentNode?.insertBefore(wf, s);
    })();
  });

Promise.all([fontLoadPromise()]).then(() => {
  // console.log("fonts loaded");
});

initHeader();

// イベントリスナー設定
window.addEventListener("resize", handleResize, { passive: false });

// ================= Top page effects =================
function run() {
  setupLoadingAnimation();

  // タッチデバイスではLenisを使用せず、ネイティブスクロールのままにする
  if (!isTouchDevice()) {
    initLenis();
  } else {
    document.documentElement.classList.remove("lenis");
    document.documentElement.classList.remove("lenis-scrolling");
    lenis = undefined;
  }

  // ページ遷移初期化
  initPageTransitions({ getLenis: () => lenis });

  const mainElement =
    document.querySelector<HTMLElement>("#swup") ||
    document.querySelector<HTMLElement>("main");
  const page = mainElement?.dataset.page;
  initPageEffects(page);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", run);
} else {
  run();
}
